import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { ShoppingBag, Menu, User, LogOut, Shield, Search, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aura-k-beauty/artifacts/5z4fhs5o_1.png";

const navLinks = [
  { to: '/', label: 'Inicio' },
  { to: '/tienda', label: 'Tienda' },
  { to: '/quiz', label: 'Skin Quiz' },
  { to: '/educacion', label: 'Skin Lab' },
  { to: '/kits', label: 'Kits' },
  { to: '/blog', label: 'Blog' },
  { to: '/nosotros', label: 'Nosotros' },
  { to: '/contacto', label: 'Contacto' },
];

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { count } = useCart();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[var(--sage)]/15" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img src={LOGO_URL} alt="Aura K Beauty" className="h-9 lg:h-11 w-auto rounded" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link text-[13px] font-medium tracking-wide transition-colors ${
                  location.pathname === link.to ? 'text-[var(--violet)] active' : 'text-[var(--dark)]/50 hover:text-[var(--dark)]'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Link to="/admin" data-testid="admin-link">
                <Button variant="ghost" size="icon" className="h-9 w-9 text-[var(--violet)]">
                  <Shield className="h-4 w-4" />
                </Button>
              </Link>
            )}
            {user && (
              <Link to="/favoritos" data-testid="wishlist-link">
                <Button variant="ghost" size="icon" className="h-9 w-9 text-[var(--dark)]/60 hover:text-red-500">
                  <Heart className="h-[18px] w-[18px]" />
                </Button>
              </Link>
            )}
            <Link to="/carrito" className="relative" data-testid="cart-link">
              <Button variant="ghost" size="icon" className="h-9 w-9 text-[var(--dark)]/60">
                <ShoppingBag className="h-[18px] w-[18px]" />
                {count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[var(--violet)] text-white text-[10px] w-4.5 h-4.5 min-w-[18px] min-h-[18px] rounded-full flex items-center justify-center font-bold leading-none" data-testid="cart-count">
                    {count}
                  </span>
                )}
              </Button>
            </Link>
            {user ? (
              <div className="hidden lg:flex items-center gap-1">
                <Link to="/mis-pedidos" data-testid="orders-link">
                  <Button variant="ghost" size="sm" className="text-[13px] text-[var(--dark)]/50 h-9 px-3">
                    <User className="h-3.5 w-3.5 mr-1.5" /> {user.name}
                  </Button>
                </Link>
                <Button variant="ghost" size="icon" onClick={logout} className="h-9 w-9" data-testid="logout-btn">
                  <LogOut className="h-3.5 w-3.5 text-[var(--dark)]/30" />
                </Button>
              </div>
            ) : (
              <Link to="/auth" className="hidden lg:block" data-testid="auth-link">
                <Button size="sm" className="bg-[var(--dark)] hover:bg-[var(--dark)]/90 text-white rounded-lg px-5 h-9 text-[13px] font-medium">
                  Ingresar
                </Button>
              </Link>
            )}

            {/* Mobile Menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-white pt-12">
                <div className="flex flex-col gap-1">
                  {navLinks.map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setOpen(false)}
                      className={`text-sm font-medium py-2.5 px-4 rounded-xl transition-colors ${
                        location.pathname === link.to ? 'bg-[var(--violet-light)] text-[var(--violet)]' : 'text-[var(--dark)]/60 hover:bg-[var(--cream)]'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <hr className="my-3 border-[var(--sage)]/15" />
                  {user ? (
                    <>
                      <Link to="/mis-pedidos" onClick={() => setOpen(false)} className="text-sm font-medium py-2.5 px-4 text-[var(--dark)]/60 rounded-xl hover:bg-[var(--cream)]">
                        Mis Pedidos
                      </Link>
                      {isAdmin && (
                        <Link to="/admin" onClick={() => setOpen(false)} className="text-sm font-medium py-2.5 px-4 text-[var(--violet)] rounded-xl hover:bg-[var(--violet-light)]">
                          Panel Admin
                        </Link>
                      )}
                      <button onClick={() => { logout(); setOpen(false); }} className="text-left text-sm font-medium py-2.5 px-4 text-red-400 rounded-xl hover:bg-red-50">
                        Cerrar Sesion
                      </button>
                    </>
                  ) : (
                    <Link to="/auth" onClick={() => setOpen(false)}>
                      <Button className="w-full bg-[var(--dark)] text-white rounded-xl h-10 text-sm">
                        Ingresar
                      </Button>
                    </Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
